import type { IEntity } from '~/features-shared/libs/interfaces/entity'

import type { IExaleadGroup } from '~/features-generic/services/interfaces/exalead-groups'

import type { SwymHTTPError } from '~/typings/SwymHTTPError'

/**
 * Generic success response of a 3DSwym API success response.
 * @info K: Type of the result.
 */
export interface SwymResponse<K> {
  nb_result?: string
  result: K
  sessionInfos?: {
    is_session_initialized_with_usergroup: boolean
  }
  sourceInfos?: Record<string, unknown>
}

/**
 * Error code of Swym APIs.
 * @info AccessDenied: User has no more access to the content.
 * @info ConcurrentEdition: Concurrent edition.
 * @info NotFound: The content or container has been deleted or not found.
 * @info UserNotPartOfCommunity: User is not part of the community.
 * @info ErrorExaleadWildcardError: The search query includes wildcard characters and is too short
 */
export enum SwymErrorCode {
  AccessDenied = 'SocialSecurityException',
  ConcurrentEdition = 'ERROR_CONCURRENT_AUTHORING',
  NotFound = 'ContainerNotFoundException',
  NullPointerException = 'NullPointerException',
  UserNotPartOfCommunity = 'ERROR_ASSIGNEE_IS_NOT_PART_OF_COMMUNITY',
  IdeaContentAlreadyLinked = 'ERROR_IDEATION_CONTENT_ALREADY_LINKED',
  ErrorFileFormatUnauthorized = 'ERROR_FILE_FORMAT_UNAUTHORIZED',
  ErrorFileSizeExceeded = 'ERROR_FILE_SIZE_EXCEEDED',
  ErrorPictureResolutionExceeded = 'ERROR_PICTURE_RESOLUTION_EXCEEDED',
  ErrorFileExtensionMismatch = 'ERROR_FILE_EXTENSION_MISMATCH',
  ErrorConferenceNoteTakerNotAuthorized = 'ERROR_CONFERENCE_NOTE_TAKER_NOT_AUTHORIZED',
  ErrorCommunityTOUNotSigned = 'ERROR_COMMUNITY_TOU_NOT_SIGNED',
  ErrorExaleadWildcardError = 'ERROR_SOCIAL_SEARCH_INDEX',
}

/** Generic error response of a 3DSwym API error response. */
export interface SwymError extends SwymHTTPError {
  errorcode?: SwymErrorCode
}

export interface NetworkResult<K> extends IEntity {
  groups: Array<K>
  users: Array<K>
}

export interface ExaleadResponse {
  nhits: number
  hits: IMetas[]
  groups: IExaleadGroup[]
}

export interface ExaleadStatsResponse {
  nmatch: number
  limitedResults: boolean
}

export interface IMetas {
  metas: IMeta[]
}

export interface IMeta {
  name: string
  value: string
}
